import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ErroDoPortal } from '../api';
import { Marca } from '../marca';
import { BotaoDeTema } from '../BotaoDeTema';

/**
 * A autenticidade do documento no Portal — `/r/<token>` (onda 315, R8).
 *
 * ─── Quem chega aqui ─────────────────────────────────────────────────
 *
 * Quem apontou a câmera para o QR da capa (ou do fecho) de um documento
 * impresso (o relatório, o laudo, o boletim de medição, o certificado): o
 * fiscal, o projetista, o cliente do cliente. Não tem conta no Portal e não
 * deveria precisar de uma para saber se o papel na mão saiu da Testesolo e
 * se ainda vale. Por isso a página fica FORA da entrada por código e não
 * pergunta `/eu`.
 *
 * ─── O que ela mostra, e o que nunca mostra ──────────────────────────
 *
 * Tipo, número, revisão, emissão, situação e o hash — o que a
 * `fn_autenticidade_publica` devolve. Cliente, obra, amostra e pessoa
 * não vêm da API e não se escrevem aqui: quem tem o token tem o papel,
 * e nele está tudo isso.
 *
 * ─── E o relatório interativo ────────────────────────────────────────
 *
 * Onda 317: no relatório do CPTu que vale (`interativo`), o link «Abrir o
 * relatório interativo» leva a `/cptu/r/<token>`, dentro do Portal — que
 * pede o login por código quando não há sessão e só abre o relatório se
 * ele for da conta (a cerca da `vw_portal_cptu`). Esta página continua
 * sem perguntar `/eu`: a autenticidade é para quem não tem conta.
 */

interface Resposta {
  tipo: string;
  tipo_nome: string;
  numero: string;
  revisao: number;
  data_emissao: string | null;
  hash_conteudo: string | null;
  situacao: string;
  /** Onda 317: há relatório interativo no Portal (o CPTu que vale). */
  interativo?: boolean;
}

interface Degrau {
  /** O símbolo do selo: a cor nunca fala sozinha. */
  simbolo: string;
  palavra: string;
  /** A classe do selo; substituído e cancelado são neutros (P-UI-41). */
  classe: 'ok' | 'neutro' | 'atencao' | 'critico';
  texto: string;
}

/**
 * A escada da autenticidade: as chaves são as da `vw_autenticidade`, e «nao_encontrado» é o 404.
 *
 * Os textos dizem «documento», e não «relatório»: desde a onda 343 todo tipo
 * da `fn_autenticidade_publica` chega aqui (relatório, laudo, boletim de
 * medição, certificado), e o nome do tipo vem escrito na ficha logo abaixo.
 */
const SITUACAO: Record<string, Degrau> = {
  valido: {
    simbolo: '✓', palavra: 'Válido', classe: 'ok',
    texto: 'Este documento foi emitido pela Testesolo e é a revisão que vale.',
  },
  substituido: {
    simbolo: '×', palavra: 'Substituído', classe: 'neutro',
    texto: 'Este documento foi emitido pela Testesolo, mas existe uma revisão mais nova que o '
      + 'substitui. Peça a revisão vigente antes de usá-lo.',
  },
  cancelado: {
    simbolo: '×', palavra: 'Cancelado', classe: 'neutro',
    texto: 'Este documento foi cancelado pela Testesolo e não deve ser usado.',
  },
  nao_emitido: {
    simbolo: '?', palavra: 'Não emitido', classe: 'atencao',
    texto: 'Este documento existe, mas ainda não foi emitido: uma cópia em circulação é preliminar.',
  },
  nao_encontrado: {
    simbolo: '!', palavra: 'Não encontrado', classe: 'critico',
    texto: 'Não encontramos nenhum documento com este código. Confira o código impresso embaixo do '
      + 'QR; se ele estiver certo, fale com a Testesolo antes de usar a cópia que você tem.',
  },
};

/** `aaaa-mm-dd` → `dd/mm/aaaa`, sem passar por `Date` (um dia não tem fuso). */
function dia(v: string | null): string {
  const m = v ? /^(\d{4})-(\d{2})-(\d{2})/.exec(v) : null;
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
}

const revisao = (n: number) => `R${String(n).padStart(2, '0')}`;

export function Autenticidade() {
  const { token = '' } = useParams();
  const [d, setD] = useState<Resposta | null>(null);
  const [naoAchou, setNaoAchou] = useState(false);
  const [falhou, setFalhou] = useState<string | null>(null);

  useEffect(() => {
    const parar = new AbortController();
    setD(null); setNaoAchou(false); setFalhou(null);
    api<Resposta>(`/autenticidade/${encodeURIComponent(token)}`, { sinal: parar.signal })
      .then(setD)
      .catch((e) => {
        if ((e as Error)?.name === 'AbortError') return;
        if (e instanceof ErroDoPortal && e.status === 404) setNaoAchou(true);
        else setFalhou('Não consegui consultar agora. Confira sua conexão e tente de novo em instantes.');
      });
    return () => parar.abort();
  }, [token]);

  const degrau = naoAchou ? SITUACAO.nao_encontrado
    : d ? (SITUACAO[d.situacao] ?? SITUACAO.nao_emitido) : null;

  return (
    <>
      <header className="faixa">
        <div className="faixa-interna">
          <Marca />
          <div className="cresce" />
          <BotaoDeTema />
        </div>
      </header>

      <main className="corpo autenticidade">
        <h1>Autenticidade do documento</h1>
        <p className="sub">
          Código <span className="mono">{token.slice(0, 40).toUpperCase()}</span>
        </p>

        {!degrau && !falhou && <p className="sub autenticidade-espera">Consultando…</p>}

        {falhou && (
          <div className="aviso ruim" role="alert"><p>{falhou}</p></div>
        )}

        {degrau && (
          <section className={`autenticidade-resposta ${degrau.classe}`} aria-live="polite">
            <span className={`selo-autenticidade ${degrau.classe}`}>
              <span className="simbolo" aria-hidden="true">{degrau.simbolo}</span> {degrau.palavra}
            </span>
            <p>{degrau.texto}</p>
          </section>
        )}

        {d && (
          <>
            <dl className="identificacao autenticidade-ficha">
              <dt>Tipo</dt><dd>{d.tipo_nome}</dd>
              <dt>Número</dt>
              <dd className="mono">{d.numero} · {revisao(d.revisao)}</dd>
              <dt>Emissão</dt><dd>{dia(d.data_emissao)}</dd>
            </dl>

            {d.hash_conteudo && (
              <div className="autenticidade-hash">
                <p className="rot">Impressão digital do arquivo (SHA-256)</p>
                <code className="mono">{d.hash_conteudo}</code>
                <p className="sub">
                  Confere, byte a byte, se o PDF que você tem é o que foi emitido: arquivos
                  diferentes têm impressões diferentes.
                </p>
              </div>
            )}

            {d.interativo && (
              <div className="autenticidade-interativo">
                <Link className="botao" to={`/cptu/r/${token.toUpperCase()}`}>Abrir o relatório interativo</Link>
                <p className="sub">
                  Perfis, ábacos, mapa, corte e 3D do relatório. Pede a entrada no Portal com o e-mail da sua empresa.
                </p>
              </div>
            )}
          </>
        )}

        <p className="sub autenticidade-privacidade">
          Esta página não informa cliente, obra, amostra nem nomes: essas informações estão no
          próprio documento, e só quem o recebeu deve tê-las.
        </p>
      </main>

      <footer className="controle">
        <div className="controle-interno">
          <p>
            Documentos controlados do Sistema de Gestão da Qualidade. A revisão mais recente de
            cada documento é a que vale.
          </p>
          <p>Testesolo Engenharia Geotécnica · Blumenau/SC · (47) 3037-2817</p>
        </div>
      </footer>
    </>
  );
}
